import { useState } from "react";
import { deleteTarget } from "@/app/deleteTarget";
import { ROLE_LABEL, useIsSuperAdmin, useMe } from "@/app/auth";
import { EntityDrawer } from "@/app/EntityDrawer";
import { EntityStatusPill } from "@/app/EntityStatusPill";
import { adminUsers, type AdminUser, type AdminUserInput } from "@/app/master";
import { loadingOf, useList } from "@/app/queries";
import { Alert, Button, EmptyState, formatTime, PageHeader, Pill, Table, TableWrap, Th } from "@/ui";
import { AdminUserForm } from "./AdminUserForm";
import { adminUserDraft, adminUserInput } from "./adminUserDraft";

/** 后台账号：管理后台的登录账号与角色（docs/04 第 9 节，决策 D43、D181）。只有超级管理员能进，至少要保留一个启用的超级管理员（#93）。 */
export function AdminUsers() {
  const me = useMe().data;
  const canWrite = useIsSuperAdmin(); // 其他角色从地址栏直接进来时：服务端 403，这里只给说明
  const [editing, setEditing] = useState<{ row: AdminUser | null } | null>(null);
  const list = useList("admin-users", adminUsers);
  const rows = list.data ?? [];

  return (
    <>
      <PageHeader title="后台账号" description="超级管理员另可管理后台账号、看审计；系统管理员可改全部配置；只读只能查看。停用或改密码立即踢出会话，至少保留一个启用的超级管理员。"
        actions={canWrite && <Button variant="primary" onClick={() => setEditing({ row: null })}>新建账号</Button>} />
      {!canWrite && <Alert title="只有超级管理员能管理后台账号" />}
      {canWrite && list.error && <Alert tone="danger" title="加载失败">{list.error.message}</Alert>}
      <TableWrap loading={canWrite && loadingOf(list)}>
        {rows.length ? (
          <Table>
            <thead><tr><Th w={240}>用户名</Th><Th w={120}>角色</Th><Th w="status">状态</Th><Th w="time">最近登录</Th><Th /></tr></thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id}>
                  <td className="ax-mono">{u.username} {u.id === me?.id && <Pill>当前登录</Pill>}</td><td>{ROLE_LABEL[u.role]}</td>
                  <td><EntityStatusPill status={u.status} /></td><td className="ax-mono ax-secondary">{u.last_login_at ? formatTime(u.last_login_at) : "从未登录"}</td>
                  <td><Button variant="ghost" size="sm" onClick={() => setEditing({ row: u })}>编辑</Button></td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          (!canWrite || !list.isPending) && <EmptyState title="没有后台账号" />
        )}
      </TableWrap>
      {editing && (
        <EntityDrawer title={editing.row ? `编辑账号 ${editing.row.username}` : "新建后台账号"} initial={adminUserDraft(editing.row ?? undefined)}
          convert={(d) => adminUserInput(d, !editing.row)}
          save={(body: AdminUserInput) => (editing.row ? adminUsers.update(editing.row.id, body) : adminUsers.create(body))}
          invalidate={["admin-users"]} message={editing.row ? "已更新后台账号" : "已新建后台账号"} deletion={editing.row ? deleteTarget("后台账号", editing.row.username, adminUsers, editing.row.id, ["admin-users"]) : undefined} onClose={() => setEditing(null)}>
          {(f) => <AdminUserForm draft={f.draft} errors={f.errors} set={f.set} isNew={!editing.row} />}
        </EntityDrawer>
      )}
    </>
  );
}
